import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { logger } from '../utils/logger';
import liveStreamService, { LiveStream } from '../services/liveStreams';
import LiveStreamPlayer from '../components/LiveStreamPlayer';
import './LiveStreamDetail.css';

const LiveStreamDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [stream, setStream] = useState<LiveStream | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadStream(id);
      // Refresh stream data every 10 seconds to update viewer count and status
      const interval = setInterval(() => loadStream(id), 10000);
      return () => clearInterval(interval);
    }
  }, [id]);

  const loadStream = async (streamId: string) => {
    try {
      const data = await liveStreamService.getLiveStreamById(streamId);
      setStream(data);
      setError(null);
    } catch (err: any) {
      logger.error('Error loading live stream:', err);
      setError(err.message || 'Failed to load live stream');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = () => {
    if (!stream) return null;

    switch (stream.status) {
      case 'live':
        return (
          <span className="status-badge live">
            <span className="pulse-dot"></span>
            LIVE
          </span>
        );
      case 'starting':
        return <span className="status-badge starting">Starting Soon</span>;
      case 'provisioning':
        return <span className="status-badge provisioning">Setting Up</span>;
      case 'stopped':
        return <span className="status-badge stopped">Stream Ended</span>;
      case 'error':
        return <span className="status-badge error">Error</span>;
      default:
        return <span className="status-badge">{stream.status}</span>;
    }
  };

  const formatViewerCount = (count: number) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="live-stream-detail-page">
        <div className="container">
          <div className="loading-state">
            <div className="loading-spinner">
              <div className="spinner-circle"></div>
              <div className="spinner-circle"></div>
              <div className="spinner-circle"></div>
            </div>
            <p>Loading stream...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !stream) {
    return (
      <div className="live-stream-detail-page">
        <div className="container">
          <div className="error-state">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
              <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="2"/>
              <path d="M32 20V36" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="32" cy="44" r="2" fill="currentColor"/>
            </svg>
            <h2>Stream not found</h2>
            <p>{error || 'The stream you\'re looking for doesn\'t exist'}</p>
            <button onClick={() => navigate('/live')} className="btn btn-primary">
              Back to Live Streams
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Stream is watchable when it's live (WebRTC streams don't need hlsPlaybackUrl)
  const canWatch = stream.status === 'live';

  return (
    <div className="live-stream-detail-page">
      <div className="container">
        <button onClick={() => navigate('/live')} className="back-button">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12 4L6 10L12 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back to Live Streams
        </button>

        {/* Player Section */}
        <div className="player-section">
          {canWatch ? (
            <LiveStreamPlayer
              streamId={stream.streamId}
              mediaType={stream.streamingMode === 'browser' ? 'audio' : 'video'}
              isLive={true}
              autoPlay={true}
            />
          ) : (
            <div className="player-placeholder">
              {stream.thumbnailUrl ? (
                <img src={stream.thumbnailUrl} alt={stream.title} />
              ) : (
                <div className="thumbnail-placeholder">
                  <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                    <circle cx="40" cy="40" r="30" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="40" cy="40" r="10" fill="currentColor"/>
                  </svg>
                </div>
              )}
              <div className="placeholder-overlay">
                {getStatusBadge()}
                {stream.status === 'stopped' && (
                  <p>This stream has ended</p>
                )}
                {stream.status === 'starting' && (
                  <p>Stream is starting...</p>
                )}
                {stream.status === 'provisioning' && (
                  <p>Stream is being set up...</p>
                )}
                {stream.status === 'error' && (
                  <p>{stream.statusMessage || 'An error occurred'}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Stream Info */}
        <div className="stream-info-section">
          <div className="stream-header">
            <div className="header-main">
              <h1 className="stream-title">{stream.title}</h1>
              <div className="stream-meta">
                <span className="stream-creator">{stream.userName || 'Tempest Radio'}</span>
                {stream.category && (
                  <>
                    <span className="meta-separator">•</span>
                    <span className="stream-category">{stream.category}</span>
                  </>
                )}
              </div>
            </div>

            <div className="header-stats">
              {getStatusBadge()}
              {stream.status === 'live' && (
                <div className="viewer-count-large">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 4C6 4 2.5 7 1 10c1.5 3 5 6 9 6s7.5-3 9-6c-1.5-3-5-6-9-6zm0 10c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4z"/>
                    <circle cx="10" cy="10" r="2"/>
                  </svg>
                  {formatViewerCount(stream.viewerCount)} watching
                </div>
              )}
            </div>
          </div>

          {stream.description && (
            <p className="stream-description">{stream.description}</p>
          )}

          {/* Tags */}
          {stream.tags && stream.tags.length > 0 && (
            <div className="stream-tags">
              {stream.tags.map((tag, index) => (
                <span key={index} className="tag">{tag}</span>
              ))}
            </div>
          )}

          {/* Stream Details Card */}
          <div className="details-card">
            <h3 className="details-title">Stream Details</h3>
            <div className="details-grid">
              <div className="detail-item">
                <span className="detail-label">Status</span>
                <span className="detail-value">{stream.status}</span>
              </div>

              {stream.startedAt && (
                <div className="detail-item">
                  <span className="detail-label">Started</span>
                  <span className="detail-value">{formatDate(stream.startedAt)}</span>
                </div>
              )}

              {stream.scheduledStartTime && stream.scheduledStartTime > Date.now() && (
                <div className="detail-item">
                  <span className="detail-label">Scheduled</span>
                  <span className="detail-value">{formatDate(stream.scheduledStartTime)}</span>
                </div>
              )}

              {stream.status === 'live' && (
                <>
                  <div className="detail-item">
                    <span className="detail-label">Current Viewers</span>
                    <span className="detail-value">{stream.viewerCount.toLocaleString()}</span>
                  </div>

                  <div className="detail-item">
                    <span className="detail-label">Peak Viewers</span>
                    <span className="detail-value">{stream.peakViewerCount.toLocaleString()}</span>
                  </div>
                </>
              )}

              <div className="detail-item">
                <span className="detail-label">Total Views</span>
                <span className="detail-value">{stream.totalViews.toLocaleString()}</span>
              </div>

              {stream.isRecordingEnabled && (
                <div className="detail-item">
                  <span className="detail-label">Recording</span>
                  <span className="detail-value recording">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                      <circle cx="6" cy="6" r="6"/>
                    </svg>
                    Enabled
                  </span>
                </div>
              )}

              <div className="detail-item">
                <span className="detail-label">Visibility</span>
                <span className="detail-value">{stream.isPublic ? 'Public' : 'Private'}</span>
              </div>
            </div>
          </div>

          {/* Creator Section */}
          <div className="creator-card">
            <div className="creator-avatar">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="2"/>
                <circle cx="24" cy="20" r="8" fill="currentColor"/>
                <path d="M8 40c0-8.8 7.2-16 16-16s16 7.2 16 16" fill="currentColor"/>
              </svg>
            </div>
            <div className="creator-info">
              <h4 className="creator-name">{stream.userName || 'Tempest Radio'}</h4>
              <p className="creator-bio">Streaming live on Tempest Radio</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveStreamDetail;
